

/**
 * Campaign video preview — visibility-gated load (feature implementation).
 * Uses Tv* primitives from @trustvid/design-system; logic stays here until TvLazyVideoPreview
 * supports wrapperStyle and matches this gate/IO behaviour in production.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TvSpinner, TvDiv, TvVideo, TvSpan, TvI } from '@trustvid/design-system';
import {
  acquirePreviewSlot,
  releasePreviewSlot,
} from '../utils/campaignPreviewLoadGate';

// Shared IntersectionObserver — one observer for all lazy video instances
const observerCallbacks = new Map(); // element -> callback
let sharedObserver = null;

function getSharedObserver(options) {
  if (!sharedObserver && typeof window !== 'undefined' && window.IntersectionObserver) {
    sharedObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const cb = observerCallbacks.get(entry.target);
        if (cb) cb(entry.isIntersecting);
      });
    }, options);
  }
  return sharedObserver;
}

function observeElement(el, callback, options) {
  const obs = getSharedObserver(options);
  if (!obs) return () => {};
  observerCallbacks.set(el, callback);
  obs.observe(el);
  return () => {
    obs.unobserve(el);
    observerCallbacks.delete(el);
  };
}

/** @internal Test-only reset for shared IntersectionObserver singleton between Jest cases. */
export function resetLazyVideoPreviewObserverForTests() {
  if (sharedObserver) {
    try {
      sharedObserver.disconnect();
    } catch {
      /* ignore */
    }
    sharedObserver = null;
  }
  observerCallbacks.clear();
}

function teardownVideoElement(videoEl) {
  if (!videoEl) return;
  try {
    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.load();
  } catch {
    /* ignore teardown errors */
  }
}

const LazyVideoPreview = ({
  src,
  poster,
  videoClassName,
  wrapperClassName,
  wrapperStyle,
  placeholderText,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  playOnlyWhenVisible = false,
  listMode = false,
  useLoadGate = false,
  eager = false,
  onDimensions,
}) => {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [canLoad, setCanLoad] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const slotHeldRef = useRef(false);

  useLayoutEffect(() => {
    if (!src || listMode) return undefined;
    setInView(false);
    setCanLoad(false);
    setFrameReady(false);

    if (eager) {
      setInView(true);
      return undefined;
    }

    const el = rootRef.current;
    if (!el) return undefined;

    if (typeof window !== 'undefined' && window.IntersectionObserver) {
      const cleanup = observeElement(
        el,
        (isVisible) => {
          if (playOnlyWhenVisible) {
            setInView(isVisible);
            return;
          }
          if (isVisible) {
            setInView(true);
          }
        },
        { rootMargin: playOnlyWhenVisible ? '40px 0px' : '80px 0px', threshold: 0.01 }
      );
      return cleanup;
    }

    setInView(true);
    return undefined;
  }, [src, playOnlyWhenVisible, listMode, eager]);

  useEffect(() => {
    if (!inView || !src || listMode) {
      setCanLoad(false);
      return undefined;
    }

    let cancelled = false;
    const run = async () => {
      if (useLoadGate) {
        await acquirePreviewSlot(src);
        if (cancelled) {
          releasePreviewSlot(src);
          return;
        }
        slotHeldRef.current = true;
      }
      if (!cancelled) setCanLoad(true);
    };
    run();

    return () => {
      cancelled = true;
      if (useLoadGate && slotHeldRef.current) {
        slotHeldRef.current = false;
        releasePreviewSlot(src);
      }
      setCanLoad(false);
      setFrameReady(false);
    };
  }, [inView, src, listMode, useLoadGate]);

  useEffect(() => {
    if (!inView) {
      setFrameReady(false);
    }
  }, [inView]);

  useEffect(() => {
    if (!frameReady || !useLoadGate || !slotHeldRef.current || !src) return;
    slotHeldRef.current = false;
    releasePreviewSlot(src);
  }, [frameReady, useLoadGate, src]);

  useEffect(() => () => teardownVideoElement(videoRef.current), []);

  useEffect(() => {
    if ((!inView || !canLoad) && videoRef.current) {
      teardownVideoElement(videoRef.current);
    }
  }, [inView, canLoad, src]);

  useEffect(() => {
    if (!frameReady || !autoPlay || !videoRef.current) return;
    const video = videoRef.current;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, [frameReady, autoPlay]);

  const baseWrap =
    wrapperClassName || 'ratio ratio-16x9 bg-light rounded-top overflow-hidden';
  const wrapClass = `${baseWrap} position-relative`;
  const vidClass = videoClassName || 'object-fit-contain w-100 h-100';
  const showPlaceholderCopy =
    placeholderText !== undefined && placeholderText !== null && String(placeholderText).length > 0;

  if (!src) {
    return null;
  }

  if (listMode) {
    return (
      <TvDiv
        ref={rootRef}
        className={`${wrapClass} d-flex align-items-center justify-content-center bg-dark bg-opacity-10`}
        style={wrapperStyle}
        data-testid="lazy-video-preview-poster"
      >
        <TvSpan className="text-muted small user-select-none d-flex flex-column align-items-center gap-1">
          <TvI className="bx bx-play-circle fs-2" aria-hidden />
          {placeholderText || 'Click to preview'}
        </TvSpan>
      </TvDiv>
    );
  }

  const fill = 'position-absolute top-0 start-0 w-100 h-100';
  const showVideo = inView && canLoad;

  return (
    <TvDiv ref={rootRef} className={wrapClass} style={wrapperStyle} data-testid="lazy-video-preview">
      {!showVideo && showPlaceholderCopy && (
        <TvDiv
          className={`${fill} d-flex align-items-center justify-content-center text-muted small px-2 text-center user-select-none bg-dark bg-opacity-50`}
        >
          <TvSpan>{placeholderText}</TvSpan>
        </TvDiv>
      )}

      {showVideo && (
        <TvVideo
          key={src}
          ref={videoRef}
          src={src}
          poster={poster || undefined}
          controls={controls}
          preload={eager ? 'auto' : 'none'}
          playsInline
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          className={`${vidClass} ${fill} ${frameReady ? 'opacity-100 z-1' : 'opacity-0'}`}
          onLoadedMetadata={(event) => {
            if (typeof onDimensions === 'function') {
              onDimensions(event.currentTarget.videoWidth, event.currentTarget.videoHeight);
            }
          }}
          onLoadedData={() => setFrameReady(true)}
          onCanPlay={() => setFrameReady(true)}
          onError={() => setFrameReady(true)}
        >
          <track kind="captions" />
        </TvVideo>
      )}

      {showVideo && !frameReady && (
        <TvDiv
          className={`${fill} d-flex align-items-center justify-content-center bg-dark bg-opacity-50 text-light small px-2 z-2`}
          data-testid="lazy-video-loading"
        >
          <TvSpinner size="sm" color="light" className="me-2" />
          <TvSpan>Loading preview…</TvSpan>
        </TvDiv>
      )}
    </TvDiv>
  );
};

LazyVideoPreview.propTypes = {
  src: PropTypes.string,
  poster: PropTypes.string,
  videoClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
  wrapperStyle: PropTypes.object,
  placeholderText: PropTypes.string,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  controls: PropTypes.bool,
  playOnlyWhenVisible: PropTypes.bool,
  listMode: PropTypes.bool,
  useLoadGate: PropTypes.bool,
  /** Skip the visibility wait and start fetching immediately. Modal playback uses this. */
  eager: PropTypes.bool,
  /** Called with (videoWidth, videoHeight) once metadata loads. */
  onDimensions: PropTypes.func,
};

export default LazyVideoPreview;
